import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  type LicenseHistoryEvent,
  getLicenseHistory,
  humanizeLicenseHistoryField,
  licenseHistoryActorLabel,
} from "@/hera-api/license-history-api";
import { formatDateTime } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";

function actionBadgeVariant(
  action: string,
): "default" | "secondary" | "destructive" | "outline" {
  if (action === "created") return "default";
  if (action === "deleted") return "destructive";
  return "secondary";
}

function LicenseHistoryEventItem({ event }: { event: LicenseHistoryEvent }) {
  return (
    <article className="rounded-md border p-3 space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant={actionBadgeVariant(event.action)}>{event.action}</Badge>
        <span className="text-sm font-medium">{event.table_label}</span>
        <span className="text-muted-foreground text-sm">
          {formatDateTime(event.changed_at)}
        </span>
      </div>
      <p className="text-sm">
        <span className="text-muted-foreground">Changed by </span>
        {licenseHistoryActorLabel(event)}
        {event.actor_user_type ? (
          <span className="text-muted-foreground">
            {" "}
            ({event.actor_user_type.toLowerCase()})
          </span>
        ) : null}
      </p>
      {event.changes.length === 0 ? (
        <p className="text-muted-foreground text-sm">
          No field details recorded.
        </p>
      ) : (
        <dl className="space-y-2">
          {event.changes.map((change) => (
            <div key={`${event.event_id}-${change.field}`} className="text-sm">
              <dt className="font-medium capitalize">
                {humanizeLicenseHistoryField(change.field)}
              </dt>
              <dd className="text-muted-foreground break-all">
                {event.action === "created" ? (
                  <span>{change.new_value ?? "—"}</span>
                ) : event.action === "deleted" ? (
                  <span>{change.old_value ?? "—"}</span>
                ) : (
                  <span>
                    {change.old_value ?? "—"} → {change.new_value ?? "—"}
                  </span>
                )}
              </dd>
            </div>
          ))}
        </dl>
      )}
    </article>
  );
}

export function LicenseHistoryDialog({
  licenseId,
  open,
  onOpenChange,
  elevated = false,
}: {
  licenseId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  elevated?: boolean;
}) {
  const historyQuery = useQuery({
    queryKey: ["getLicenseHistory", licenseId],
    queryFn: () => getLicenseHistory(licenseId),
    enabled: open && Boolean(licenseId),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent elevated={elevated} className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>License history</DialogTitle>
          <DialogDescription>
            Who changed this license, comments, payments, and inspections, and
            what fields were set. Derived cache updates are omitted.
          </DialogDescription>
        </DialogHeader>
        {historyQuery.isLoading ? (
          <p className="text-muted-foreground text-sm">Loading history…</p>
        ) : historyQuery.isError ? (
          <p className="text-destructive text-sm">
            {historyQuery.error instanceof Error
              ? historyQuery.error.message
              : "Failed to load license history"}
          </p>
        ) : (historyQuery.data?.events.length ?? 0) === 0 ? (
          <p className="text-muted-foreground text-sm">
            No audited changes were found for this license.
          </p>
        ) : (
          <ScrollArea className="max-h-[70vh] pr-3">
            <div className="space-y-3">
              {historyQuery.data?.truncated ? (
                <p className="text-muted-foreground text-sm">
                  Showing the {historyQuery.data.events.length} most recent
                  changes.
                </p>
              ) : null}
              {historyQuery.data?.events.map((event) => (
                <LicenseHistoryEventItem key={event.event_id} event={event} />
              ))}
            </div>
          </ScrollArea>
        )}
      </DialogContent>
    </Dialog>
  );
}
